import { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { API_URL } from '../../constants/apiUrls';
import { showSnackBar } from '../../../redux/snackBarSlice';
import UI from '../../constants/ui';
import { putRequest } from '../../services';

function EventRegistrationForm(props) {
  const dispatch = useDispatch();
  const {
    companyName,
    eventName,
    eventID,
    startDate,
    endDate,
    assessmentIds = [],
    technologies,
    loading,
    interviewSkills,
    assessmentCount,
    jobId,
  } = props;

  const [formData, setFormData] = useState({
    technologies: [],
  });
  const [error, setError] = useState('');
  const [applyJob, setApplyJob] = useState(true);

  // Memoize matched technologies
  const matchedTechnologies = useMemo(
    () =>
      assessmentIds?.length
        ? technologies.filter((option) => assessmentIds.includes(option.value))
        : [],
    [assessmentIds, technologies]
  );

  useEffect(() => {
    const newTechnologies = matchedTechnologies.map((tech) => tech.value);
    // Only update if formData.technologies is empty
    if (formData.technologies?.length === 0 && newTechnologies?.length > 0) {
      setFormData((prev) => ({
        ...prev,
        technologies: newTechnologies,
      }));
    }
  }, [matchedTechnologies]);

  const assessmentSkillIds = formData.technologies.join(',');

  const onSubmit = (e) => {
    e.preventDefault();
    if (formData.technologies?.length === 0) {
      setError('At least one skill must be selected!');
      return;
    }
    try {
      let url = `${API_URL.EVENT_REGISTRATION}/${eventID}/registration`;

      if (assessmentSkillIds) {
        url += `?assessmentIds=${assessmentSkillIds}`;
      }

      if (applyJob) {
        url += '&applyJob=true';
      } else {
        url += '&applyJob=false';
      }

      putRequest(url)
        .then((res) => {
          dispatch(
            showSnackBar({
              setopen: true,
              message: res.msg,
              severity: 'success',
            })
          );
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        })
        .catch((err) => {
          dispatch(
            showSnackBar({
              setopen: true,
              message: err.msg,
              severity: 'error',
            })
          );
        });
    } catch (err) {
      dispatch(
        showSnackBar({
          setopen: true,
          message: err.msg,
          severity: 'error',
        })
      );
    }
  };

  return (
    <Box
      component="section"
      sx={{
        p: 2.5,
        maxWidth: 400,
        '& .MuiTypography-root': { lineHeight: 1.4 },
      }}
    >
      {companyName !== 'Global' && (
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>{UI.COMPANY}:</strong> {companyName}
        </Typography>
      )}

      <Typography variant="body1" sx={{ mb: 1 }}>
        <strong>{UI.EVENT_NAME}:</strong> {eventName}
      </Typography>

      <Typography variant="body1" sx={{ mb: 1 }}>
        <strong>{UI.START_DATE}:</strong> {startDate}
      </Typography>

      <Typography variant="body1" sx={{ mb: 1 }}>
        <strong>{UI.END_DATE}:</strong> {endDate}
      </Typography>

      <Box
        component="form"
        onSubmit={onSubmit}
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {/* Assessments */}
        <FormControl fullWidth error={!!error}>
          <Typography
            component="label"
            htmlFor="assessment-select"
            sx={{
              fontSize: 17,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              mb: 1,
            }}
          >
            {`${UI.ASSESSMENTS}:`}
            {assessmentIds?.length === 0 && (
              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                *
              </Box>
            )}
          </Typography>

          {assessmentIds?.length > 0 && matchedTechnologies?.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {matchedTechnologies.map((tech) => (
                <Box
                  key={tech.value}
                  component="span"
                  sx={{
                    bgcolor: 'grey.100',
                    px: 1,
                    py: 0.5,
                    borderRadius: 3,
                    fontSize: 14,
                  }}
                >
                  #{tech.label}
                </Box>
              ))}
            </Box>
          ) : (
            <>
              <Autocomplete
                multiple
                id="assessment-select"
                options={technologies}
                getOptionLabel={(opt) => opt.label}
                filterSelectedOptions
                loading={loading}
                onChange={(_, selectedOptions) => {
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((o) => o.value)
                    : [];
                  if (
                    JSON.stringify(selectedValues) !==
                    JSON.stringify(formData.technologies)
                  ) {
                    setFormData((prev) => ({
                      ...prev,
                      technologies: selectedValues,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select assessments."
                  />
                )}
              />

              {error ? (
                <FormHelperText>{error}</FormHelperText>
              ) : (
                <FormHelperText sx={{ color: '#0E70B3' }}>
                  {UI.REQUIRED_SKILLS.replace('{n}', assessmentCount)}
                </FormHelperText>
              )}
            </>
          )}
        </FormControl>

        {/* Interview Skills */}
        {interviewSkills?.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              component="strong"
              sx={{ fontSize: 17, fontWeight: 500 }}
            >
              {`${UI.INTERVIEWS}:`}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {interviewSkills.map((skill) => (
                <Box
                  key={skill}
                  component="span"
                  sx={{
                    bgcolor: 'grey.100',
                    px: 1,
                    py: 0.5,
                    borderRadius: 3,
                    fontSize: 14,
                    whiteSpace: 'nowrap',
                  }}
                >
                  #{skill}
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {jobId ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={applyJob}
                onChange={(e) => setApplyJob(e.target.checked)}
                color="primary"
              />
            }
            label="This event is linked to a job posting. Would you like to automatically apply for the job once you successfully complete the assessments?"
          />
        ) : null}

        <Button type="submit" variant="contained" size="large">
          {UI.REGISTER_}
        </Button>
      </Box>
    </Box>
  );
}

export default EventRegistrationForm;

EventRegistrationForm.propTypes = {
  companyName: PropTypes.string.isRequired,
  eventName: PropTypes.string.isRequired,
  eventID: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  assessmentIds: PropTypes.arrayOf(PropTypes.string),
  technologies: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  interviewSkills: PropTypes.arrayOf(PropTypes.string),
  assessmentCount: PropTypes.number.isRequired,
  jobId: PropTypes.string,
};

EventRegistrationForm.defaultProps = {
  assessmentIds: [],
  interviewSkills: [],
  jobId: null,
};
